import {
  all,
  call,
  getContext,
  put,
  takeEvery,
  select,
  takeLatest,
} from "redux-saga/effects";

import { CONTEXT } from "@const/redux";
import {
  changeSubscriptionPositionActionTypes,
  createSubscriptionActionTypes,
  deleteMySubscriptionActionTypes,
  editSubscriptionActionTypes,
  getPortionSubscriptionActionTypes,
  getSubscriptionActionTypes,
  getSubscriptionDetailsActionTypes,
  getSubscriptionsByPostActionTypes,
} from "./actionTypes";
import {
  changeSubscriptionPositionFailure,
  changeSubscriptionPositionSuccess,
  createSubscriptionFailure,
  createSubscriptionSuccess,
  deleteMySubscriptionFailure,
  deleteMySubscriptionSuccess,
  editSubscriptionSuccess,
  getPortionSubscriptionsFailure,
  getPortionSubscriptionsSuccess,
  getSubscriptionDetailsFailure,
  getSubscriptionDetailsSuccess,
  getSubscriptionFailure,
  getSubscriptionsByPostFailure,
  getSubscriptionsByPostSuccess,
  getSubscriptionSuccess,
} from "./actions";
import { setAuthors } from "../authors/actions";
import {
  normalizeSubscription,
  normalizeSubscriptionArray,
} from "@helpers/subscriptions";
import {
  addMySubscription,
  removeMySubscription,
  reorderSubscriptions,
  setMySubscription,
} from "../profile/actions";
import {
  setCreatorSubscription,
  setCreatorSubscriptionPortion,
} from "../creatorProfile/actions";
import { cancelSubscriptionActionTypes } from "../payments/actionTypes";
import {
  cancelSubscriptionFailure,
  cancelSubscriptionSuccess,
} from "../payments/actions";
import { setSubscriptionDetails } from "../subscriptionDetails/actions";
import { attachPostsToSubscription } from "../createSubscriptionPost/actions";
import { completeMultipartUpload, uploadFilesSaga } from "../upload/saga";

const createSubscriptionAsync = ({ SubscriptionsService, subscription }) => {
  if (!SubscriptionsService) {
    console.log("Required services does not exist");

    return;
  }

  return SubscriptionsService.createSubscription(subscription);
};

const uploadSubscriptionPosterAsync = async ({ fileIds }) => {
  return fileIds[0];
};

function* createSubscription({ payload }) {
  try {
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);
    const {
      image,
      includedPostIds,
      excludedPostIds,
      selectType,
      ...subscriptionModel
    } = payload;

    const { data } = yield call(createSubscriptionAsync, {
      SubscriptionsService,
      subscription: { ...subscriptionModel },
    });

    const { result, entities } = normalizeSubscription(data);

    if (includedPostIds?.length || excludedPostIds?.length || selectType) {
      yield put(
        attachPostsToSubscription({
          id: result,
          model: {
            includedPostIds,
            excludedPostIds,
            selectType,
          },
        }),
      );
    }

    const { totalSubscriptions = 0 } = yield select(
      ({ profileReducer }) => profileReducer,
    );

    yield all([
      put(createSubscriptionSuccess(entities)),
      put(
        addMySubscription({
          subscriptions: [result],
          totalCount: totalSubscriptions + 1,
        }),
      ),
    ]);
  } catch ({ error }) {
    console.log(`Create sub error:: message -> ${error?.message}`);
    yield put(createSubscriptionFailure(error));
  }
}

const getSubscriptionAsync = ({ SubscriptionsService, username, params }) => {
  if (!SubscriptionsService) {
    console.log("Required services does not exist");

    return;
  }

  return SubscriptionsService.getSubscription(username, params);
};

function* getSubscription({ payload }) {
  try {
    const { username, ...params } = payload;
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);
    const { data, pagination } = yield call(getSubscriptionAsync, {
      SubscriptionsService,
      username,
      params,
    });

    const { result, entities } = normalizeSubscriptionArray(data);

    const { username: myUsername } = yield select(
      ({ profileReducer }) => profileReducer,
    );

    const { nextOffset, totalCount } = pagination;

    yield all([
      put(getSubscriptionSuccess(entities)),
      username === myUsername
        ? put(setMySubscription({ subscriptions: result, totalCount }))
        : put(
            setCreatorSubscription({
              subscription: result,
              totalCount,
              nextOffset,
            }),
          ),
    ]);
  } catch ({ error }) {
    yield put(getSubscriptionFailure(error));
  }
}

function* getSubscriptionPortion({ payload }) {
  try {
    const { username, ...params } = payload;
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);
    const { data, pagination } = yield call(getSubscriptionAsync, {
      SubscriptionsService,
      username,
      params,
    });

    const { result, entities } = normalizeSubscriptionArray(data);

    const { username: myUsername } = yield select(
      ({ profileReducer }) => profileReducer,
    );

    const { nextOffset, totalCount } = pagination;

    yield all([
      put(getPortionSubscriptionsSuccess(entities)),
      username === myUsername
        ? put(addMySubscription({ subscriptions: result, totalCount }))
        : put(
            setCreatorSubscriptionPortion({
              subscription: result,
              totalCount,
              nextOffset,
            }),
          ),
    ]);
  } catch ({ error }) {
    yield put(getPortionSubscriptionsFailure(error));
  }
}

const editSubscriptionAsync = ({ SubscriptionsService, id, subscription }) => {
  if (!SubscriptionsService) {
    console.log("Required services does not exist");

    return;
  }

  return SubscriptionsService.editSubscription(id, subscription);
};

function* editSubscription({ payload }) {
  try {
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);
    const {
      id,
      image,
      imageId,
      includedPostIds,
      excludedPostIds,
      selectType,
      ...subscriptionModel
    } = payload;

    let newImageId;

    let fileIds = [];

    if (image) {
      const { FilesService } = yield getContext(CONTEXT.SERVICES);
      const uploadedFiles = yield call(uploadFilesSaga, [image]);

      yield call(completeMultipartUpload, { FilesService, uploadedFiles });
      fileIds = uploadedFiles.map(({ fileId }) => fileId);
      newImageId = yield call(uploadSubscriptionPosterAsync, { fileIds });
    }

    const { data } = yield call(editSubscriptionAsync, {
      id,
      SubscriptionsService,
      subscription: { ...subscriptionModel, imageId: newImageId || imageId },
    });

    const { entities } = normalizeSubscription(data);

    if (includedPostIds?.length || excludedPostIds?.length || selectType) {
      yield put(
        attachPostsToSubscription({
          id,
          model: {
            includedPostIds,
            excludedPostIds,
            selectType,
          },
        }),
      );
    }

    yield put(editSubscriptionSuccess(entities));
  } catch ({ error }) {
    yield put(createSubscriptionFailure(error));
  }
}

const getSubscriptionDetailsAsync = ({ SubscriptionsService, uniqueKey }) => {
  if (!SubscriptionsService) {
    return;
  }

  return SubscriptionsService.getSubscriptionById(uniqueKey);
};

function* getSubscriptionDetails({ payload }) {
  try {
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);
    const { uniqueKey } = payload;

    const { data } = yield call(getSubscriptionDetailsAsync, {
      SubscriptionsService,
      uniqueKey,
    });

    const { entities, result } = normalizeSubscription(data);
    const { subscriptions, authors } = entities;

    yield all([
      put(setAuthors(authors)),
      put(
        setSubscriptionDetails({
          ...subscriptions[result],
          authorDetails: authors[subscriptions[result].author],
        }),
      ),
      put(getSubscriptionDetailsSuccess(subscriptions)),
    ]);
  } catch ({ error }) {
    yield put(getSubscriptionDetailsFailure(error));
  }
}

const cancelSubscriptionAsync = ({ SubscriptionsService, id }) => {
  if (!SubscriptionsService) {
    console.log("Required services does not exist");

    return null;
  }

  return SubscriptionsService.cancelSubscription(id);
};

function* cancelSubscription({ payload }) {
  try {
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);

    yield call(cancelSubscriptionAsync, {
      SubscriptionsService,
      id: payload,
    });

    yield put(cancelSubscriptionSuccess(payload));
  } catch ({ error }) {
    yield put(cancelSubscriptionFailure(error));
  }
}

const getSubscriptionsByPostAsync = ({ PostsService, id, limit, offset }) => {
  return PostsService.getPostSubscriptions(id, { limit, offset });
};

function* getSubscriptionsByPost({ payload }) {
  try {
    const { id, limit, offset } = payload;
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    const { data } = yield call(getSubscriptionsByPostAsync, {
      PostsService,
      id,
      limit,
      offset,
    });

    const { result, entities } = normalizeSubscriptionArray(data);

    yield put(
      getSubscriptionsByPostSuccess({
        result,
        subscriptions: entities.subscriptions,
      }),
    );
  } catch ({ error }) {
    yield put(getSubscriptionsByPostFailure(error));
  }
}

const deleteMySubscriptionAsync = ({ SubscriptionsService, id }) => {
  return SubscriptionsService.deleteSubscription(id);
};

function* deleteMySubscription({ payload }) {
  try {
    const { id } = payload;
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);

    yield call(deleteMySubscriptionAsync, {
      SubscriptionsService,
      id,
    });

    yield all([
      put(removeMySubscription({ id })),
      put(deleteMySubscriptionSuccess({ id })),
    ]);
  } catch ({ error }) {
    yield put(deleteMySubscriptionFailure(error));
  }
}

const changeSubscriptionPositionAsync = ({
  SubscriptionsService,
  id,
  position,
}) => {
  if (!SubscriptionsService) {
    console.log("Required service is missing");
  }

  return SubscriptionsService.updateOrder(id, { position });
};

function* changeSubscriptionPosition({ payload }) {
  const { oldIndex, newIndex } = payload;

  try {
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);
    // update UI
    const id = yield select(
      ({ profileReducer }) => profileReducer.subscriptions?.[oldIndex],
    );
    yield put(reorderSubscriptions({ oldIndex, newIndex }));

    // REST API update
    yield call(changeSubscriptionPositionAsync, {
      id,
      SubscriptionsService,
      position: newIndex + 1,
    });

    yield put(changeSubscriptionPositionSuccess());
  } catch ({ error }) {
    // revert UI, set error
    yield all([
      put(reorderSubscriptions({ oldIndex, newIndex })),
      put(changeSubscriptionPositionFailure()),
    ]);
  }
}

export function* watchCreateSubscription() {
  yield takeEvery(createSubscriptionActionTypes.request, createSubscription);
}

export function* watchEditSubscription() {
  yield takeEvery(editSubscriptionActionTypes.request, editSubscription);
}

export function* watchGetSubscription() {
  yield takeEvery(getSubscriptionActionTypes.request, getSubscription);
}

export function* watchGetSubscriptionPortion() {
  yield takeLatest(
    getPortionSubscriptionActionTypes.request,
    getSubscriptionPortion,
  );
}

export function* watchGetSubscriptionDetails() {
  yield takeEvery(
    getSubscriptionDetailsActionTypes.request,
    getSubscriptionDetails,
  );
}

export function* watchCancelSubscription() {
  yield takeEvery(cancelSubscriptionActionTypes.request, cancelSubscription);
}

export function* watchGetSubscriptionsByPost() {
  yield takeEvery(
    getSubscriptionsByPostActionTypes.request,
    getSubscriptionsByPost,
  );
}

export function* watchGetDeleteMySubscription() {
  yield takeEvery(
    deleteMySubscriptionActionTypes.request,
    deleteMySubscription,
  );
}

export function* watchChangePosition() {
  yield takeEvery(
    changeSubscriptionPositionActionTypes.request,
    changeSubscriptionPosition,
  );
}
